import React from 'react';
import { getLocalDateKey } from './dateUtils';
import { getFlexiblePrecision, isTimelineAllDayTask } from './scheduleUtils';
import { TimelineDraftSelection } from './TimelineDraftSelection';
import { TimelineHourTask } from './TimelineHourTask';
import { useTimelineView } from './TimelineViewContext';
import {
    getEndTimeForRange, getHourTaskLayouts, getPeriodDate, getTimelineHourOffset, getTimelineHourRows,
    getTimelineTaskRenderSegment,
} from './timelineViewUtils';

export const TimelineDayOverlay: React.FC<{ periodDate: Date }> = ({ periodDate }) => {
    const {
        addTaskDate, addTaskDuration, addTaskTime, currentMouseY, draggingTask,
        dragStartHour, dragStartOffset, dragTaskStartTopY, dragTaskStartY, draftSelection,
        getTasksForDay, getTimelineDateFromClientY, isDragging, selectedDate, tasksWithDueDates,
        timelineWindow, timeSlotHeight,
    } = useTimelineView();
    const timedTasks = getTasksForDay(periodDate).filter(({ task }) => !getFlexiblePrecision(task) && !isTimelineAllDayTask(task));
    const visibleDraft = draftSelection && getLocalDateKey(draftSelection.date) === getLocalDateKey(periodDate)
        ? draftSelection : null;
    if (draggingTask && currentMouseY !== null && dragTaskStartY !== null && dragTaskStartTopY !== null) {
        const taskTopY = dragTaskStartTopY + currentMouseY - dragTaskStartY;
        const preview = getTimelineDateFromClientY(taskTopY);
        const dragged = tasksWithDueDates.find(({ task }) => task.id === draggingTask.task.id);
        if (preview && dragged && getLocalDateKey(preview) === getLocalDateKey(periodDate)
            && !timedTasks.some(({ task }) => task.id === draggingTask.task.id)) {
            timedTasks.push(dragged);
        }
    }
    const firstLoadedDay = getPeriodDate(selectedDate, 'day', timelineWindow.start);
    const isFirstLoadedDay = getLocalDateKey(firstLoadedDay) === getLocalDateKey(periodDate);
    const renderedTasks = timedTasks.filter(({ task }) => {
        return getTimelineTaskRenderSegment(task, periodDate, isFirstLoadedDay) !== null
            || (draggingTask?.task.id === task.id && currentMouseY !== null);
    });
    const now = new Date();
    const hourRows = getTimelineHourRows();
    const getOffset = (date: Date) => getTimelineHourOffset(date.getHours(), date.getMinutes(), hourRows, timeSlotHeight);
    const currentPosition = getOffset(now);
    const layouts = getHourTaskLayouts(renderedTasks, periodDate);
    return (
        <>
            {periodDate.toDateString() === now.toDateString() && (
                <>
                    <div className="current-hour-line timeline-current-hour-line" style={{ top: `${currentPosition}px` }} />
                    <span className="timeline-current-time-label" style={{ top: `${currentPosition - 10}px` }}>
                        {now.getHours().toString().padStart(2, '0')}:{now.getMinutes().toString().padStart(2, '0')}
                    </span>
                </>
            )}
            {isDragging && addTaskDate && getLocalDateKey(addTaskDate) === getLocalDateKey(periodDate)
                && dragStartHour !== null && (
                <div className="drag-preview timeline-hour-drag-preview"
                    style={{ top: `${getTimelineHourOffset(dragStartHour, 0, hourRows, timeSlotHeight) + dragStartOffset}px`, height: `${addTaskDuration / 60 * timeSlotHeight}px` }}>
                    <div className="drag-preview-content">
                        <span className="drag-preview-time">
                            {addTaskTime} - {addTaskTime ? getEndTimeForRange(addTaskTime, addTaskDuration) : ''}
                        </span>
                    </div>
                </div>
            )}
            {visibleDraft && <TimelineDraftSelection selection={visibleDraft}
                top={getTimelineHourOffset(Number(visibleDraft.startTime.slice(0, 2)), Number(visibleDraft.startTime.slice(3)), hourRows, timeSlotHeight)} />}
            {renderedTasks.map((taskInfo) => (
                <TimelineHourTask key={taskInfo.task.id} taskInfo={taskInfo} periodDate={periodDate}
                    getOffset={getOffset} layout={layouts.get(taskInfo.task.id)} />
            ))}
        </>
    );
};
